import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import axios from "../../config/axios";
import Toast from '../../shared/toast/Toast';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import {
    CreateScreeningQuestionsRequest,
    GetScreeningAnswersRequest,
    PendingQuestion,
    RequireNationalIdRequest,
    ReorderOptionsRequest,
    ReorderOptionsResponse,
    ReorderQuestionsRequest,
    ScreeningAnswersResponse,
    ScreeningFiltersResponse,
    ScreeningQuestionsResponse,
    ScreeningQuestionsStoreInterface,
    SuggestScreeningQuestionsRequest,
    SuggestScreeningQuestionsResponse,
    ValidateScreeningQuestionsRequest
} from '../../utils/interfaces/screening-questions/ScreeningQuestionsInterfaces';

const appKey = 'screeningQuestions';
const apiModuleUrl = 'pa/ft/screening-questions';
const jobPostsUrl = 'pa/ft/job-posts';

// Async Thunks
export const suggestScreeningQuestions = createAsyncThunk(
    `${appKey}/suggestScreeningQuestions`,
    async (data: SuggestScreeningQuestionsRequest) => {
        const response = await axios.post<ResponseResult<SuggestScreeningQuestionsResponse>>(
            `${apiModuleUrl}/suggest`,
            data
        );
        return response.data.data;
    }
);

export const createScreeningQuestions = createAsyncThunk(
    `${appKey}/createScreeningQuestions`,
    async (data: CreateScreeningQuestionsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<ScreeningQuestionsResponse>>(
                `${apiModuleUrl}/create`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ details: error?.details ?? [], message: error?.message ?? '' });
        }
    }
);

export const getScreeningQuestions = createAsyncThunk(
    `${appKey}/getScreeningQuestions`,
    async (jobPostExternalId: string) => {
        const response = await axios.get<ResponseResult<ScreeningQuestionsResponse>>(
            `${apiModuleUrl}/job-post/${jobPostExternalId}`
        );
        return response.data.data;
    }
);

export const getScreeningQuestionsFilters = createAsyncThunk(
    `${appKey}/getScreeningQuestionsFilters`,
    async (jobPostExternalId: string) => {
        const response = await axios.get<ResponseResult<ScreeningFiltersResponse>>(
            `${apiModuleUrl}/filters/${jobPostExternalId}`
        );
        return response.data.data;
    }
);

export const deleteScreeningQuestion = createAsyncThunk(
    `${appKey}/deleteScreeningQuestion`,
    async (questionExternalIds: string[]) => {
        await axios.delete<ResponseResult<null>>(
            apiModuleUrl,
            { data: { questionExternalIds } }
        );
        return questionExternalIds;
    }
);

export const reorderScreeningQuestions = createAsyncThunk(
    `${appKey}/reorderScreeningQuestions`,
    async (data: ReorderQuestionsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.put<ResponseResult<ScreeningQuestionsResponse>>(
                `${apiModuleUrl}/reorder`,
                data
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message ?? '' });
        }
    }
);

export const reorderQuestionOptions = createAsyncThunk(
    `${appKey}/reorderQuestionOptions`,
    async (data: ReorderOptionsRequest) => {
        const response = await axios.put<ResponseResult<ReorderOptionsResponse>>(
            `${apiModuleUrl}/options/reorder`,
            data
        );
        return response.data.data;
    }
);

export const getScreeningAnswers = createAsyncThunk(
    `${appKey}/getScreeningAnswers`,
    async (params: GetScreeningAnswersRequest) => {
        const response = await axios.get<ResponseResult<ScreeningAnswersResponse>>(
            `${apiModuleUrl}/answers/${params.applicationExternalId}`,
            {
                params: {
                    applicationType: params.applicationType,
                },
            }
        );
        return response.data.data;
    }
);

export const updateJobRequireNationalId = createAsyncThunk(
    `${appKey}/updateJobRequireNationalId`,
    async ({ jobId, requireNationalId }: { jobId: string } & RequireNationalIdRequest, { rejectWithValue }) => {
        try {
            const response = await axios.put<ResponseResult<null>>(
                `${jobPostsUrl}/${jobId}/require-national-id`,
                { requireNationalId }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue({ message: error?.message ?? '' });
        }
    }
);

export const validateScreeningQuestions = createAsyncThunk(
    `${appKey}/validateScreeningQuestions`,
    async (data: ValidateScreeningQuestionsRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<null>>(
                `${apiModuleUrl}/validate`,
                data
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue({ details: error?.details ?? [], message: error?.message ?? '' });
        }
    }
);

// Initial State
const initialState: ScreeningQuestionsStoreInterface = {
    questions: [],
    suggestedQuestions: [],
    filters: [],
    answers: [],
    pendingQuestions: [],
    jobRequiresNationalId: false,
    isLoadingQuestions: false,
    isLoadingSuggestions: false,
    isCreatingQuestions: false,
    isDeletingQuestion: false,
    isReorderingQuestions: false,
    isReorderingOptions: false,
    isLoadingFilters: false,
    isLoadingAnswers: false,
    isValidatingQuestions: false,
    validationErrorUids: [],
    validationErrorMessage: '',
    validationErrorMessages: {},
    pendingReorderIds: null,
    pendingDeleteIds: [],
};

// Slice
export const screeningQuestionsSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {
        addPendingQuestion: (state, action: PayloadAction<PendingQuestion>) => {
            state.pendingQuestions.push(action.payload);
        },
        updatePendingQuestion: (state, action: PayloadAction<PendingQuestion>) => {
            const index = state.pendingQuestions.findIndex(q => q.uid === action.payload.uid);
            if (index !== -1) {
                state.pendingQuestions[index] = action.payload;
                state.validationErrorUids = (state.validationErrorUids ?? []).filter(uid => uid !== action.payload.uid);
                // Ensure validationErrorMessages is always an object
                if (!state.validationErrorMessages) {
                    state.validationErrorMessages = {};
                }
                delete state.validationErrorMessages[action.payload.uid];
            }
        },
        setValidationErrorUids: (state, action: PayloadAction<string[]>) => {
            state.validationErrorUids = action.payload;
        },
        setValidationErrorMessages: (state, action: PayloadAction<Record<string, string>>) => {
            state.validationErrorMessages = action.payload || {};
        },
        reorderPendingQuestions: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
            const [removed] = state.pendingQuestions.splice(action.payload.fromIndex, 1);
            state.pendingQuestions.splice(action.payload.toIndex, 0, removed);
        },
        removePendingQuestion: (state, action: PayloadAction<string>) => {
            state.pendingQuestions = state.pendingQuestions.filter(q => q.uid !== action.payload);
        },
        setJobRequiresNationalId: (state, action: PayloadAction<boolean>) => {
            state.jobRequiresNationalId = action.payload;
        },
        clearPendingQuestions: (state) => {
            state.pendingQuestions = [];
            state.pendingReorderIds = null;
        },
        setPendingReorderIds: (state, action: PayloadAction<string[] | null>) => {
            state.pendingReorderIds = action.payload;
        },
        addPendingDeleteId: (state, action: PayloadAction<string>) => {
            state.pendingDeleteIds.push(action.payload);
        },
        clearPendingDeleteIds: (state) => {
            state.pendingDeleteIds = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Suggest Screening Questions
            .addCase(suggestScreeningQuestions.pending, (state) => {
                state.isLoadingSuggestions = true;
            })
            .addCase(suggestScreeningQuestions.fulfilled, (state, action) => {
                state.isLoadingSuggestions = false;
                state.suggestedQuestions = action.payload.questions;
            })
            .addCase(suggestScreeningQuestions.rejected, (state, action) => {
                state.isLoadingSuggestions = false;
                Toast.Error({ content: action.error?.message || t('error_suggesting_questions') });
            })

            // Create Screening Questions
            .addCase(createScreeningQuestions.pending, (state) => {
                state.isCreatingQuestions = true;
            })
            .addCase(createScreeningQuestions.fulfilled, (state, action) => {
                state.isCreatingQuestions = false;
                state.questions = [...state.questions, ...action.payload.questions];
                Toast.Success({ content: t('screening_questions_created') });
            })
            .addCase(createScreeningQuestions.rejected, (state, action) => {
                state.isCreatingQuestions = false;
                const payload = action.payload as { details?: Array<{index: number, message: string, code?: string}>; message?: string } | undefined;
                if (!payload?.details?.length) {
                    Toast.Error({ content: payload?.message || t('error_creating_questions') });
                } else {
                    const isMHRSD = payload.message === 'screening.error.MHRSDGuidelinesViolated';
                    state.validationErrorMessage = isMHRSD ? 'screening_questions_MHRSDGuidelinesViolated' : (payload.message ?? '');
                }
            })

            // Get Screening Questions
            .addCase(getScreeningQuestions.pending, (state) => {
                state.isLoadingQuestions = true;
                state.questions = [];
                state.pendingQuestions = [];
            })
            .addCase(getScreeningQuestions.fulfilled, (state, action) => {
                state.isLoadingQuestions = false;
                state.questions = action.payload.questions;
            })
            .addCase(getScreeningQuestions.rejected, (state, action) => {
                state.isLoadingQuestions = false;
                Toast.Error({ content: action.error?.message || t('error_loading_questions') });
            })

            // Get Screening Questions Filters
            .addCase(getScreeningQuestionsFilters.pending, (state) => {
                state.isLoadingFilters = true;
            })
            .addCase(getScreeningQuestionsFilters.fulfilled, (state, action) => {
                state.isLoadingFilters = false;
                state.filters = action.payload.filters;
            })
            .addCase(getScreeningQuestionsFilters.rejected, (state, action) => {
                state.isLoadingFilters = false;
                Toast.Error({ content: action.error?.message || t('error_loading_filters') });
            })

            // Delete Screening Question
            .addCase(deleteScreeningQuestion.pending, (state) => {
                state.isDeletingQuestion = true;
            })
            .addCase(deleteScreeningQuestion.fulfilled, (state, action) => {
                state.isDeletingQuestion = false;
                state.questions = state.questions.filter(
                    (q) => !action.payload.includes(q.externalId as string)
                );
                state.pendingDeleteIds = [];
                Toast.Success({ content: t('screening_question_deleted') });
            })
            .addCase(deleteScreeningQuestion.rejected, (state, action) => {
                state.isDeletingQuestion = false;
                Toast.Error({ content: action.error?.message || t('error_deleting_question') });
            })

            // Reorder Screening Questions
            .addCase(reorderScreeningQuestions.pending, (state) => {
                state.isReorderingQuestions = true;
            })
            .addCase(reorderScreeningQuestions.fulfilled, (state, action) => {
                state.isReorderingQuestions = false;
                state.questions = action.payload.questions;
                Toast.Success({ content: t('screening_questions_reordered') });
            })
            .addCase(reorderScreeningQuestions.rejected, (state, action) => {
                state.isReorderingQuestions = false;
                Toast.Error({ content: action.error?.message || t('error_reordering_questions') });
            })

            // Reorder Question Options
            .addCase(reorderQuestionOptions.pending, (state) => {
                state.isReorderingOptions = true;
            })
            .addCase(reorderQuestionOptions.fulfilled, (state, action) => {
                state.isReorderingOptions = false;
                const updatedQuestion = action.payload.question;
                state.questions = state.questions.map((q) =>
                    q.externalId === updatedQuestion.externalId ? updatedQuestion : q
                );
                Toast.Success({
                  content: t("screening_questions_options_reordered"),
                });
            })
            .addCase(reorderQuestionOptions.rejected, (state, action) => {
                state.isReorderingOptions = false;
                Toast.Error({ content: action.error?.message || t('error_reordering_options') });
            })

            // Get Screening Answers
            .addCase(getScreeningAnswers.pending, (state) => {
                state.isLoadingAnswers = true;
            })
            .addCase(getScreeningAnswers.fulfilled, (state, action) => {
                state.isLoadingAnswers = false;
                state.answers = action.payload.answers;
            })
            .addCase(getScreeningAnswers.rejected, (state, action) => {
                state.isLoadingAnswers = false;
                Toast.Error({ content: action.error?.message || t('error_loading_answers') });
            })

            // Validate Screening Questions
            .addCase(validateScreeningQuestions.pending, (state) => {
                state.isValidatingQuestions = true;
                state.validationErrorUids = [];
                state.validationErrorMessage = '';
                state.validationErrorMessages = {};
            })
            .addCase(validateScreeningQuestions.fulfilled, (state) => {
                state.isValidatingQuestions = false;
                state.validationErrorUids = [];
                state.validationErrorMessage = '';
                state.validationErrorMessages = {};
            })
            .addCase(validateScreeningQuestions.rejected, (state, action) => {
                state.isValidatingQuestions = false;
                const payload = action.payload as { details?: Array<{index: number, message: string, code?: string}>; message?: string } | undefined;
                if (!payload?.details?.length) {
                    Toast.Error({ content: action.error?.message || t('error_validating_questions') });
                } else {
                    const isMHRSD = payload.message === 'screening.error.MHRSDGuidelinesViolated';
                    state.validationErrorMessage = isMHRSD ? 'screening_questions_MHRSDGuidelinesViolated' : (payload.message ?? '');
                }
            })

            // Update Job Require National ID
            .addCase(updateJobRequireNationalId.rejected, (_state, action) => {
                const payload = action.payload as { message?: string } | undefined;
                Toast.Error({ content: payload?.message || t('error_updating_national_id_requirement') });
            });
    },
});

export const { addPendingQuestion, updatePendingQuestion, reorderPendingQuestions, removePendingQuestion, clearPendingQuestions, setJobRequiresNationalId, setValidationErrorUids, setValidationErrorMessages, setPendingReorderIds, addPendingDeleteId, clearPendingDeleteIds } = screeningQuestionsSlice.actions;

export default screeningQuestionsSlice.reducer;
